import React from "react";
import { Box, Container, Grid, Typography, Link, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from "react-router-dom";
import Logo from '../../assets/images/sportplatzgarage_dark.svg';
import { openCookieSettings } from '../CookieBanner/CookieBanner';
import FacebookIcon from '@material-ui/icons/Facebook';
import TwitterIcon from '@material-ui/icons/Twitter';
import InstagramIcon from '@material-ui/icons/Instagram';

const useStyles = makeStyles((theme) => ({
  footer: {
    backgroundColor: '#1b4995',
    color: '#ffffff',
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: 'auto',
    marginBottom: 0,
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: theme.spacing(6, 3, 4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 2, 3),
    },
  },
  logo: {
    height: '40px',
    marginBottom: theme.spacing(2),
    filter: 'brightness(0) invert(1)',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(2),
    color: '#ffffff',
  },
  link: {
    color: 'rgba(255,255,255,0.85)',
    textDecoration: 'none',
    display: 'block',
    marginBottom: theme.spacing(1),
    transition: 'color 0.2s ease',
    '&:hover': {
      color: '#f0bc17',
    },
  },
  socialLink: {
    display: 'flex',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.85)',
    textDecoration: 'none',
    marginBottom: theme.spacing(1.5),
    transition: 'color 0.2s ease, transform 0.2s ease',
    '&:hover': {
      color: '#f0bc17',
      transform: 'translateX(4px)',
    },
  },
  socialIcon: {
    marginRight: theme.spacing(1),
    fontSize: '1.5rem',
  },
  content: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: '0.9rem',
    lineHeight: 1.7,
    '& a': {
      color: '#f0bc17',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  copyright: {
    backgroundColor: '#143875',
    padding: theme.spacing(2),
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
    textAlign: 'center',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '0.85rem',
    borderTop: '1px solid rgba(255,255,255,0.12)',
  },
  divider: {
    margin: 0,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
}));

export default function Footer(props) {
  const classes = useStyles();

  const navigationLinks = [
    { label: "Startseite", href: "/" },
    { label: "Nachrichten", href: "/nachrichten" },
    { label: "Dienstleistungen", href: "/dienstleistungen" },
    { label: "VW · Audi · SEAT · Škoda · CUPRA", href: "/vag-garage-davos/" },
    { label: "Online Termin", href: "/online-termin" },
    { label: "Garage", href: "/garage" },
  ];

  const socialLinks = [
    { 
      label: "Facebook", 
      href: "https://www.facebook.com/sportplatzgarage/", 
      icon: <FacebookIcon className={classes.socialIcon} />
    },
    { 
      label: "Instagram", 
      href: "https://www.instagram.com/sportplatzgarage_gutknecht/", 
      icon: <InstagramIcon className={classes.socialIcon} />
    },
    { 
      label: "Twitter", 
      href: "https://twitter.com/sportplzgarage", 
      icon: <TwitterIcon className={classes.socialIcon} />
    },
  ];

  return (
    <Box component="footer" className={`${classes.footer} spg-footer`}>
      <Container maxWidth="lg" className={classes.container}>
        <Grid container spacing={4}>
          {/* Logo & Rechtliches */}
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <img className={classes.logo} src={Logo} alt="Sportplatzgarage Gutknecht – stop+go Davos" />
              <Box mt={2}>
                <Link 
                  component={RouterLink} 
                  to='/impressum' 
                  className={classes.link}
                >
                  Impressum
                </Link>
                <Link 
                  component={RouterLink} 
                  to='/datenschutz' 
                  className={classes.link}
                >
                  Datenschutz
                </Link>
                <button
                  type="button"
                  className={classes.link}
                  style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                  onClick={() => openCookieSettings()}
                >
                  Cookie-Einstellungen
                </button>
              </Box>
              <Box mt={2}>
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    className={classes.socialLink}
                  >
                    {social.icon}
                    <Typography variant="body2">{social.label}</Typography>
                  </a>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Navigation */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className={classes.title}>
              Navigation
            </Typography>
            {navigationLinks.map((link) => (
              <Link
                key={link.label}
                component={RouterLink}
                to={link.href}
                className={classes.link}
              >
                {link.label}
              </Link>
            ))}
          </Grid>

          {/* Kontakt */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className={classes.title}>
              Kontakt
            </Typography>
            <Box 
              className={classes.content}
              dangerouslySetInnerHTML={{__html: props.footerText}}
            />
          </Grid>

          {/* Partner */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className={classes.title}>
              Partner
            </Typography>
            <Box
              className={`${classes.content} spg-partner`}
              dangerouslySetInnerHTML={{__html: props.partnerText}}
            />
          </Grid>
        </Grid>
      </Container>
      
      <Divider className={classes.divider} />
      
      <Box className={classes.copyright}>
        Sportplatzgarage Gutknecht GMBH © {new Date().getFullYear()}
      </Box>
    </Box>
  );
}
